import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DataTableFacetedFilter } from '@/components/data-table-faceted-filter';
import { DataTableViewByDate } from '@/components/data-table-view-by-date';
import SearchInput from '@/components/search-input';
import {
  useBanners,
  usePlacements,
  useStatuses,
  useStores,
} from '@/ui/screens/PromotionList/hooks';

export default function PromotionListTableHeader() {
  const [searchParams, setSearchParams] = useSearchParams();
  const bannerIds = searchParams.getAll('banner');
  const search = searchParams.get('search') ?? '';
  const statuses = searchParams.getAll('status');
  const storeIds = searchParams.getAll('store');
  const zoneIds = searchParams.getAll('zone');
  const { data: banners } = useBanners();
  const { data: placements } = usePlacements();
  const { data: statusFilters } = useStatuses();
  const { data: stores } = useStores();
  const filteredStores =
    bannerIds.length > 0
      ? stores?.filter((s) => bannerIds.includes(s.banner_id.toString()))
      : stores;
  const showReset =
    bannerIds.length > 0 ||
    search.length > 0 ||
    statuses.length > 0 ||
    storeIds.length > 0 ||
    zoneIds.length > 0;

  useEffect(() => {
    if (storeIds.length > 0) {
      const filteredStoreIds =
        filteredStores?.map((s) => s.id.toString()) ?? [];
      const invalidStoreIds = storeIds.filter(
        (s) => !filteredStoreIds.includes(s),
      );

      if (invalidStoreIds.length > 0) {
        setSearchParams((prev) => {
          invalidStoreIds.forEach((id) => prev.delete('store', id));
          return prev;
        });
      }
    }
  }, [filteredStores, setSearchParams, storeIds]);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <SearchInput autoFocus />
      <DataTableFacetedFilter
        options={
          statusFilters?.map((s) => ({
            label: s.display_name,
            value: s.name,
          })) ?? []
        }
        queryKey="status"
        title="Status"
      />
      <DataTableFacetedFilter
        options={
          banners?.map((b) => ({
            label: b.name,
            value: b.id.toString(),
          })) ?? []
        }
        queryKey="banner"
        title="Banners"
      />
      <DataTableFacetedFilter
        options={
          filteredStores?.map((s) => ({
            label: s.name,
            value: s.id.toString(),
          })) ?? []
        }
        queryKey="store"
        title="Stores"
      />
      <DataTableFacetedFilter
        options={
          placements?.map((p) => ({
            label: p.name,
            value: p.id.toString(),
          })) ?? []
        }
        queryKey="zone"
        title="Zones"
      />
      <DataTableViewByDate />
      {showReset && (
        <Button onClick={() => setSearchParams({})} variant="ghost">
          Reset
          <X />
        </Button>
      )}
    </div>
  );
}
